import { IconHouseBed16 } from '@warp-ds/icons/react';
import { Button } from '@warp-ds/react/components/button';

import { ImageCarousel } from 'shared/components/ImageCarousel/ImageCarousel';
import { useConfig } from 'shared/config/ConfigContext';
import { thousandSeparated } from 'shared/util/formatting';

import { useUnleash } from '@/hooks/unleash';
import type { Filters, Offer } from '@/interfaces';
import { formatDistances, getNumberOfPax, getPricePerPerson, getPriceTotal } from '@/lib/utils/hotelData';
import { addHotelPageFiltersToUrl, browserQueryParamFor } from '@/lib/utils/queryParamFilterConversions';
import { trackClickOfferResult, trackSeeMoreOffers, trackTicketOfferClick, trackTripxOfferClick } from '@/lib/utils/tracking';
import displayValues from '@/resources/displayValues.json';
import { SupplierLogo } from '../images/SupplierLogo';
import { FlightDateAndTime } from './FlightDateAndTime';
import { StarRating } from './StarRating';

const meals: Record<string, string> = displayValues.amenities.meals;

export interface ResultListItemProps {
    offer: Offer;
    filters: Filters;
    variant: string | undefined;
}

export const ResultListItem = ({ offer, filters, variant }: ResultListItemProps) => {
    const { contextPathPP, imgPath } = useConfig();
    const unleash = useUnleash();
    const isExposed = typeof variant === 'string' && variant.length > 0;

    const numPax = getNumberOfPax(offer.numberOfAdults, offer.numberOfChildren);
    const pricePerPerson = thousandSeparated(getPricePerPerson(offer.pricePerPerson, numPax, offer.priceTotal));
    const priceTotal = thousandSeparated(getPriceTotal(offer.pricePerPerson, numPax, offer.priceTotal));

    const hotelUrl = addHotelPageFiltersToUrl(`${contextPathPP}/hotell?${browserQueryParamFor('hotelId', offer.hotelId)}`, filters);

    const joinLocation = (...parts: Array<string | null | undefined>) =>
        parts
            .map((s) => s?.trim())
            .filter((s): s is string => !!s && s.length > 0)
            .join(', ');

    const images = offer.imagePaths.map((path) => `${imgPath}480w${path}`);

    const trackExperimentClick = () => {
        if (offer.supplier === 'ticket') {
            trackTicketOfferClick(offer.offerId, offer.hotelName, 'Open');
        }
        if (offer.supplier === 'tripx') {
            trackTripxOfferClick(offer.offerId, offer.hotelName, 'Open');
        }
    };

    return (
        <article
            className="flex flex-col border s-border rounded-8 md:flex-row items-stretch"
            data-testid={`resultListItem-${offer.offerId}`}
            data-result-item
            tabIndex={-1}>
            <div className="md:w-1/2 w-full md:max-w-[400px] md:max-h-[220px] overflow-hidden rounded-tl-8 rounded-tr-8 md:rounded-tr-0 md:rounded-bl-8">
                {unleash?.imageCarousel ? (
                    <ImageCarousel images={images} alt={offer.hotelName ?? 'Hotellbilde'} className="h-full" />
                ) : (
                    <img src={images[0]} alt={offer.hotelName ?? 'Hotellbilde'} className="w-full h-full object-cover" />
                )}
            </div>
            <div className="flex flex-col p-16 w-full">
                <header className="flex flex-col gap-8 md:flex-row-reverse md:justify-between ">
                    <div className="flex flex-row items-center gap-8 justify-between md:flex-col-reverse md:gap-16 md:items-end md:h-fit">
                        <StarRating id={`rating-${offer.offerId}`} rating={offer.rating} emptyStars={true} />
                        <SupplierLogo supplier={offer.supplier} />
                    </div>
                    <div className="flex flex-col gap-8">
                        <h2 className="t4 mb-0" data-testid="offerHotelName" id={`resultListItem-${offer.offerId}`}>
                            {offer.hotelName}
                        </h2>
                        <p className="s-text-subtle text-xs mb-0" data-testid="offerDestination">
                            {joinLocation(offer.destination, offer.region, offer.country)}
                        </p>
                        <FlightDateAndTime departureDate={offer.departureDate} returnDate={offer.returnDate} />
                    </div>
                </header>

                <p className="flex flex-row items-center gap-4 text-detail font-bold" data-testid="offerDuration">
                    <IconHouseBed16 aria-hidden="true" /> {`${offer.duration} dager`}
                    {offer.board && offer.board !== 'no_meals' && (
                        <span className="font-normal" data-testid="offerMeal">
                            • {meals[offer.board]}
                        </span>
                    )}
                </p>

                <div className="flex flex-col gap-8 md:flex-row md:justify-between md:items-end">
                    <p className="s-text-subtle text-detail" data-testid="offerDistance">
                        {formatDistances(offer.distanceToBeach, offer.distanceToCity)}
                    </p>
                    <p className="flex t5 md:t6">{`${priceTotal} kr/${numPax} personer`}</p>
                </div>

                <footer className="flex w-full items-stretch gap-4 md:items-center md:justify-between">
                    <Button
                        className="text-xs sm:text-s flex-[2_1_0%] min-w-[14rem] max-w-none md:flex-none md:basis-auto md:shrink-0 md:w-auto md:min-w-0 md:border-none md:px-0 hover:!bg-transparent hover:!underline"
                        secondary
                        href={hotelUrl}
                        onClick={() => {
                            if (isExposed) {
                                trackExperimentClick();
                            }
                            trackSeeMoreOffers(offer.offerId, offer.hotelName);
                        }}
                        rel="nofollow"
                        data-testid={`resultDetailLink-${offer.hotelId}`}>
                        Se flere reisealternativer
                        <span className="sr-only"> for {offer.hotelName}</span>
                    </Button>
                    <Button
                        primary
                        className="text-s flex-[1_1_0%] min-w-0 max-w-none md:flex-none md:basis-auto md:shrink-0 md:w-auto"
                        href={`${contextPathPP}/ut?offerId=${offer.offerId}&fromPage=result${filters.numberOfAdults ? `&numberOfAdults=${filters.numberOfAdults}` : ''}${filters.agesOfChildren?.length ? `&agesOfChildren=${filters.agesOfChildren.join(',')}` : ''}`}
                        onClick={() => {
                            if (isExposed) {
                                trackExperimentClick();
                            }
                            trackClickOfferResult(offer);
                        }}
                        data-testid="offerLink"
                        rel="nofollow noopener"
                        target="_blank">
                        {pricePerPerson} kr
                    </Button>
                </footer>
            </div>
        </article>
    );
};
